import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { programRef } from '../../../lib/access/types';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { IntelligenceFailureNotice } from '../_shared/components/IntelligenceFailureNotice';
import { IntelligenceReextractButton } from '../_shared/components/IntelligenceReextractButton';
import { EmptyState } from '../_shared/components/PageStates';
import { ProposalReview } from '../_shared/components/ProposalReview';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useProgram } from '../Programs/hooks';
import { FiscalQuarterPicker } from './FiscalQuarterPicker';
import { useDeleteQASPScore, useQASPScores, useUpsertQASPScore } from './hooks';
import { narrativeFieldBindings, narrativeFormField } from './narrativeFields';
import { OverallDerivation } from './overallDerivation';
import { ScoreAttachmentLink } from './ScoreAttachmentLink';
import { ScoreDocumentUpload } from './ScoreDocumentUpload';
// The per-area narratives this tab binds a form field to, in form order. Feeds
// QASP_FIELD_BINDINGS below, the textarea `fields` entries (placed under each
// area's own score), and qaspDetailSections' drawer inline edit — one source
// of truth for all three (SAD-154/SAD-161: QASP and CPARS must not drift).
const QASP_NARRATIVE_AREAS = [
    {
        destination: IntelligenceTypes.DestinationFieldName.TECHNICAL_NARRATIVE,
        formField: 'technicalNarrative',
        label: 'Technical narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.SCHEDULE_NARRATIVE,
        formField: 'scheduleNarrative',
        label: 'Schedule narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.COST_STAFFING_NARRATIVE,
        formField: 'costStaffingNarrative',
        label: 'Cost / staffing narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.MANAGEMENT_NARRATIVE,
        formField: 'managementNarrative',
        label: 'Management narrative'
    }
];
// Maps the QASP extractor's canonical destination fields onto this form's
// react-hook-form field names. Date fields need the YYYY-MM-DD string shape
// the existing date inputs use; score fields need numbers.
const QASP_FIELD_BINDINGS = {
    [IntelligenceTypes.DestinationFieldName.QUARTER]: {
        formField: 'quarter',
        label: 'Quarter',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.PERIOD_START]: {
        formField: 'reportPeriodStart',
        label: 'Period start',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.PERIOD_END]: {
        formField: 'reportPeriodEnd',
        label: 'Period end',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.OVERALL_SCORE]: {
        formField: 'overallScore',
        label: 'Overall score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.TECHNICAL_SCORE]: {
        formField: 'technicalScore',
        label: 'Technical score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_SCORE]: {
        formField: 'scheduleScore',
        label: 'Schedule score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.COST_STAFFING_SCORE]: {
        formField: 'costStaffingScore',
        label: 'Cost / staffing score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_SCORE]: {
        formField: 'managementScore',
        label: 'Management score',
        parse: (v)=>Number(v)
    },
    ...narrativeFieldBindings(QASP_NARRATIVE_AREAS)
};
// Column order mirrors CPARS (identifier · Start · End · Overall) so the two
// quality tables read consistently side by side.
// Report-period columns are historical evaluation windows, not deadlines, so
// they right-align (as dates do) but carry no urgency coloring.
const columns = [
    {
        key: 'quarter',
        header: 'Quarter'
    },
    {
        key: 'reportPeriodStart',
        header: 'Start',
        align: 'right',
        render: (row)=>row.reportPeriodStart ? formatLocalYmd(row.reportPeriodStart) ?? '—' : '—'
    },
    {
        key: 'reportPeriodEnd',
        header: 'End',
        align: 'right',
        render: (row)=>row.reportPeriodEnd ? formatLocalYmd(row.reportPeriodEnd) ?? '—' : '—'
    },
    {
        key: 'overallScore',
        header: 'Overall',
        align: 'right',
        render: (row)=>row.overallScore != null ? row.overallScore.toFixed(2) : '—',
        maskAs: 'qasp_score'
    }
];
// Fiscal-quarter helper text on the date fields: the picker (rendered in
// extraContent, below every declarative field — EntityFormDialog only hands
// live form access to extraContent, not topContent, see FiscalQuarterPicker's
// doc comment) fills these in when a quarter is picked, but they stay
// directly editable either way.
const PERIOD_HELP_TEXT = 'Auto-filled by the fiscal quarter picker below, or enter directly.';
// The four per-area ratings that roll up into Overall. Order matches the form.
// Overall itself is excluded: it is the result, not an input to itself.
const QASP_CATEGORY_SCORE_FIELDS = [
    'technicalScore',
    'scheduleScore',
    'costStaffingScore',
    'managementScore'
];
const fields = [
    {
        kind: 'date',
        name: 'reportPeriodStart',
        label: 'Period start',
        required: true,
        helpText: PERIOD_HELP_TEXT
    },
    {
        kind: 'date',
        name: 'reportPeriodEnd',
        label: 'Period end',
        required: true,
        helpText: PERIOD_HELP_TEXT
    },
    // Overall carries no narrative of its own — it's the derived result, not
    // one of the four rated areas.
    {
        kind: 'number',
        name: 'overallScore',
        label: 'Overall (0–5)',
        min: 0,
        max: 5,
        step: 0.01
    },
    {
        kind: 'number',
        name: 'technicalScore',
        label: 'Technical',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(QASP_NARRATIVE_AREAS[0]),
    {
        kind: 'number',
        name: 'scheduleScore',
        label: 'Schedule',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(QASP_NARRATIVE_AREAS[1]),
    {
        kind: 'number',
        name: 'costStaffingScore',
        label: 'Cost / staffing',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(QASP_NARRATIVE_AREAS[2]),
    {
        kind: 'number',
        name: 'managementScore',
        label: 'Management',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(QASP_NARRATIVE_AREAS[3])
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const qaspDefaults = (q)=>({
        id: q?.id ?? '',
        quarter: q?.quarter ?? '',
        // No `?? 0` fallback: a missing score must render blank, not zero.
        overallScore: q?.overallScore,
        technicalScore: q?.technicalScore,
        scheduleScore: q?.scheduleScore,
        costStaffingScore: q?.costStaffingScore,
        managementScore: q?.managementScore,
        reportPeriodStart: q?.reportPeriodStart ? formatLocalYmd(q.reportPeriodStart) ?? '' : '',
        reportPeriodEnd: q?.reportPeriodEnd ? formatLocalYmd(q.reportPeriodEnd) ?? '' : '',
        // Seeded from the record (not just left blank) so an inline edit that never
        // touches a narrative still re-sends it — the upsert below is a full
        // replace, and this default is the only thing standing between an
        // unrelated score edit and silently wiping every narrative.
        technicalNarrative: q?.technicalNarrative ?? '',
        scheduleNarrative: q?.scheduleNarrative ?? '',
        costStaffingNarrative: q?.costStaffingNarrative ?? '',
        managementNarrative: q?.managementNarrative ?? ''
    });
// Read sections plus, per row, the inline-edit control. Rows the create form
// covers carry an `editField` (bound to the drawer's form), including the
// per-area narratives now that the create form exposes them; the get-well
// plan and the attachment stay read-only even in edit mode (they ride through
// the upsert from the source row, not re-keyed here). Score rows keep their
// `masked` flag for the READ display: only a writer (canEdit) enters edit,
// and writers see full values, so surfacing the number control is no leak.
function qaspDetailSections(q) {
    const score = (n)=>n != null ? n.toFixed(2) : '—';
    const ymd = (t)=>t ? formatLocalYmd(t) ?? '—' : '—';
    const scoreField = (name, label)=>({
            kind: 'number',
            name,
            label,
            min: 0,
            max: 5,
            step: 0.01
        });
    return [
        {
            heading: 'Report period',
            rows: [
                {
                    label: 'Start',
                    value: ymd(q.reportPeriodStart),
                    editField: {
                        kind: 'date',
                        name: 'reportPeriodStart',
                        label: 'Period start',
                        required: true
                    }
                },
                {
                    label: 'End',
                    value: ymd(q.reportPeriodEnd),
                    editField: {
                        kind: 'date',
                        name: 'reportPeriodEnd',
                        label: 'Period end',
                        required: true
                    }
                }
            ]
        },
        {
            heading: 'Scores',
            rows: [
                {
                    label: 'Overall',
                    value: score(q.overallScore),
                    masked: true,
                    editField: scoreField('overallScore', 'Overall (0–5)')
                },
                {
                    label: 'Technical',
                    value: score(q.technicalScore),
                    masked: true,
                    editField: scoreField('technicalScore', 'Technical')
                },
                {
                    label: 'Schedule',
                    value: score(q.scheduleScore),
                    masked: true,
                    editField: scoreField('scheduleScore', 'Schedule')
                },
                {
                    label: 'Cost / staffing',
                    value: score(q.costStaffingScore),
                    masked: true,
                    editField: scoreField('costStaffingScore', 'Cost / staffing')
                },
                {
                    label: 'Management',
                    value: score(q.managementScore),
                    masked: true,
                    editField: scoreField('managementScore', 'Management')
                }
            ]
        },
        {
            heading: 'Narratives',
            rows: [
                {
                    label: 'Technical',
                    value: q.technicalNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(QASP_NARRATIVE_AREAS[0], 2)
                },
                {
                    label: 'Schedule',
                    value: q.scheduleNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(QASP_NARRATIVE_AREAS[1], 2)
                },
                {
                    label: 'Cost / staffing',
                    value: q.costStaffingNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(QASP_NARRATIVE_AREAS[2], 2)
                },
                {
                    label: 'Management',
                    value: q.managementNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(QASP_NARRATIVE_AREAS[3], 2)
                },
                // Not exposed by the create form; preserved through the upsert.
                {
                    label: 'Get-well plan',
                    value: q.getWellPlan || '—',
                    masked: true
                }
            ]
        },
        // Masked: the source PDF holds the very scores we redact, so a restricted
        // viewer must not be able to download it. Read-only in edit mode — the
        // attachment id rides through the upsert from parent state.
        ...q.attachmentId ? [
            {
                heading: 'Attachment',
                rows: [
                    {
                        label: 'Document',
                        value: /*#__PURE__*/ _jsx(ScoreAttachmentLink, {
                            attachmentId: q.attachmentId
                        }),
                        masked: true,
                        maskFieldName: 'attachment_id'
                    }
                ]
            }
        ] : []
    ];
}
export function QASPScoresTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = useQASPScores(programId);
    const upsert = useUpsertQASPScore();
    const remove = useDeleteQASPScore();
    // Delete is an exclusive allow-list (program manager / quality, per Matt's
    // review comment) computed server-side as the same "close" decision the
    // delete RPC enforces — never re-derive it client-side. Unlike
    // SubcontractorReviewsTab (which merely disables Delete for a caller
    // without canManageQuality), an unauthorized persona here must not even
    // see the affordance, so it's hidden entirely rather than disabled.
    const canDeleteQualityScore = useProgram(programId).data?.canDeleteQualityScore ?? false;
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Holds the uploaded document's artifact id for the open surface. Seeded from
    // the edited row so the full-replace upsert (on artifact_id) doesn't drop it.
    const [attachmentId, setAttachmentId] = useState('');
    const [attachedName, setAttachedName] = useState('');
    const [uploading, setUploading] = useState(false);
    // Regenerated on each create-drawer open so it stays stable for that one
    // open (ProposalReview passes it through, unchanged, to MarkRunAppliedToDraft).
    const [draftId, setDraftId] = useState('');
    // The run this draft's own Re-extract button just started, so the review
    // surface pins to it instead of the awaiting/failed-run queries' most
    // recent hit for this target - which, for a create dialog, has no entityId
    // yet and is shared across every open "New QASP Score" dialog (SAD-321,
    // SAD-154). '' (not undefined) is the sentinel for "no run started yet for
    // this draft" (the first-run "Extract" verb below): IntelligenceFailureNotice
    // and ProposalReview both treat ANY defined pinnedRunId - including '' - as
    // "pinned, never fall back to the shared target's most recent run", so
    // leaving this undefined between opens would let a fresh dialog fall back
    // to, and immediately show, a FAILED/awaiting-review run some OTHER open
    // "New QASP Score" dialog left behind for the same shared target. Once a
    // run actually starts, '' still means "started but no id back yet" - both
    // components already treat it identically to any other non-matching pin, so
    // there is no separate state to add. Mirrors DeviationsTab's
    // pinnedDeviationRunId, which never used undefined for this reason either.
    // Reset alongside draftId whenever a fresh create drawer opens.
    const [pinnedRunId, setPinnedRunId] = useState('');
    const openNew = ()=>{
        setAttachmentId('');
        setAttachedName('');
        setUploading(false);
        setDraftId(crypto.randomUUID());
        setPinnedRunId('');
        setFormOpen(true);
    };
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). The get-well plan isn't exposed by either surface, so
    // it's carried through from the source row so the full-replace upsert
    // doesn't wipe it. Quarter is picker-owned (see below) but still falls back
    // to the source row the same way.
    const submitQASP = async (values, id)=>{
        const src = id ? items.find((q)=>q.id === id) ?? null : null;
        const quarter = values.quarter ?? src?.quarter ?? '';
        // 'quarter' is no longer a declarative FormFieldConfig entry (the picker
        // owns it via setValue), so it carries no RHF `required` rule of its own
        // — enforce the same "must pick a quarter" guarantee the old free-text
        // field's required:true gave, surfaced the same way EntityForm already
        // shows a server rejection (inline submitError).
        if (!quarter) {
            throw new Error('Pick a fiscal year and quarter.');
        }
        await upsert.mutateAsync({
            id,
            programId,
            attachmentId,
            quarter,
            // A blank score field is `undefined` (see EntityForm's number input),
            // which must reach pangaea as an unset field, not a coerced 0.
            overallScore: values.overallScore,
            technicalScore: values.technicalScore,
            scheduleScore: values.scheduleScore,
            costStaffingScore: values.costStaffingScore,
            managementScore: values.managementScore,
            reportPeriodStart: parseLocalDate(values.reportPeriodStart),
            reportPeriodEnd: parseLocalDate(values.reportPeriodEnd),
            // Now bound to real form fields (create form + drawer inline edit): an
            // untouched narrative round-trips from `values` (itself seeded from the
            // source row by qaspDefaults), never fabricated. `src` is the fallback
            // only for a value the form never had a chance to seed at all.
            technicalNarrative: values.technicalNarrative ?? src?.technicalNarrative ?? '',
            scheduleNarrative: values.scheduleNarrative ?? src?.scheduleNarrative ?? '',
            costStaffingNarrative: values.costStaffingNarrative ?? src?.costStaffingNarrative ?? '',
            managementNarrative: values.managementNarrative ?? src?.managementNarrative ?? '',
            // Not exposed by either surface; preserve the source row's value so the
            // full-replace upsert doesn't wipe it.
            getWellPlan: src?.getWellPlan ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync({
            programId,
            id: deleting.id
        });
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: qaspDefaults(detail),
        onSubmit: (v)=>submitQASP(v, detail.id),
        isSubmitting: upsert.isPending || uploading,
        submitLabel: 'Save changes',
        // Seed the attachment id from the record so an untouched edit re-sends
        // it (the drawer offers no re-upload control).
        onEditStart: ()=>setAttachmentId(detail.attachmentId ?? '')
    } : undefined;
    const newButton = /*#__PURE__*/ _jsx(SpecraButton, {
        onClick: openNew,
        disabled: !canEdit,
        "data-testid": Testids.deliveryQuality.qaspCreate,
        children: "New QASP Score"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("h3", {
                        className: "text-[15px] font-medium",
                        children: [
                            "QASP Scores ",
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-normal text-muted-foreground",
                                children: [
                                    "· ",
                                    items.length
                                ]
                            })
                        ]
                    }),
                    newButton
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryQuality.qaspTable,
                rowTestid: Testids.deliveryQuality.qaspRow,
                rowKey: (q)=>q.id,
                isLoading: isLoading,
                hideRowActions: !canEdit && !canDeleteQualityScore,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                resource: programRef(programId),
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No QASP scores yet",
                    description: "Quarterly self-assessments against the QASP (scored 0–5) drive the Satisfaction KPI. Record the first quarter's ratings and attach the source document.",
                    action: !canEdit ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: openNew,
                        children: "New QASP Score"
                    })
                }),
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryQuality.qaspEdit,
                                children: "Edit"
                            }),
                            canDeleteQualityScore && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryQuality.qaspDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New QASP Score",
                fields: fields,
                defaultValues: qaspDefaults(null),
                onSubmit: async (v)=>{
                    await submitQASP(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending || uploading,
                extraContent: (form)=>/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(OverallDerivation, {
                                form: form,
                                categoryFields: QASP_CATEGORY_SCORE_FIELDS,
                                overallField: "overallScore"
                            }),
                            /*#__PURE__*/ _jsx(FiscalQuarterPicker, {
                                form: form,
                                quarterField: "quarter",
                                startField: "reportPeriodStart",
                                endField: "reportPeriodEnd"
                            }),
                            /*#__PURE__*/ _jsx(ScoreDocumentUpload, {
                                label: "Upload QASP document",
                                onUploaded: (id, name)=>{
                                    setAttachmentId(id);
                                    setAttachedName(name);
                                },
                                attachedName: attachedName,
                                onUploadingChange: setUploading
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceReextractButton, {
                                workflowKind: IntelligenceTypes.WorkflowKind.QASP,
                                target: {
                                    programId,
                                    entityType: 'QASPScore'
                                },
                                sourceArtifactId: attachmentId,
                                canEdit: canEdit,
                                label: pinnedRunId === '' ? 'Extract from document' : 'Re-extract from document',
                                description: pinnedRunId === '' ? 'Processes the attached source document, right here in this drawer, to propose field values.' : 'Re-runs field extraction against the attached source document.',
                                verb: pinnedRunId === '' ? 'Extract' : 'Re-extract',
                                // '' means "started, but no run id yet" - ProposalReview and
                                // IntelligenceFailureNotice both render no panel for it and
                                // never fall back to another run for this shared target.
                                onStarted: (run)=>setPinnedRunId(run?.id ?? '')
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceFailureNotice, {
                                target: {
                                    programId,
                                    entityType: 'QASPScore'
                                },
                                workflowKind: IntelligenceTypes.WorkflowKind.QASP,
                                pinnedRunId: pinnedRunId
                            }),
                            /*#__PURE__*/ _jsx(ProposalReview, {
                                target: {
                                    programId,
                                    entityType: 'QASPScore'
                                },
                                workflowKind: IntelligenceTypes.WorkflowKind.QASP,
                                draftId: draftId,
                                pinnedRunId: pinnedRunId,
                                fieldBindings: QASP_FIELD_BINDINGS,
                                onApplyToDraft: (values)=>{
                                    for (const { formField, value } of values){
                                        form.setValue(formField, value);
                                    }
                                }
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? `QASP Score — ${detail.quarter}` : '',
                sections: detail ? qaspDetailSections(detail) : [],
                resource: programRef(programId),
                maskAs: "qasp_score",
                testid: Testids.deliveryQuality.scoreDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                children: (form)=>form.editing ? /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Fiscal quarter"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-2",
                                children: /*#__PURE__*/ _jsx(FiscalQuarterPicker, {
                                    form: form,
                                    quarterField: "quarter",
                                    startField: "reportPeriodStart",
                                    endField: "reportPeriodEnd"
                                })
                            })
                        ]
                    }) : null
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete QASP Score",
                description: deleting ? `Delete the QASP score for ${deleting.quarter || 'this quarter'}? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
